import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Upload,
  message,
  Popconfirm,
  Typography,
  Pagination,
  Checkbox,
  Image,
  Row,
  Col,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  MessageOutlined,
  FileExcelOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import axios from "axios";
import ProductTableSkeleton from "../../components/Skeleton/ProductTableSkeleton";
import BulkUploadModal from "../BulkUpload/BulkProductUpload";
import { useProductStore } from "../../store/useProductStore";
import "./Products.css";
import "./CreateProduct.css";

const { Option } = Select;

const Products = () => {
  const {
    products,
    vendors,
    shapes,
    colors,
    cases,
    loading,
    error,
    fetchData,
    total,
    catalogueTotal,
    totalPages,
  } = useProductStore();

  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);

  // Create new name modal
  const [showCreateNameModal, setShowCreateNameModal] = useState(false);
  const [apiPrefix, setApiPrefix] = useState("");
  const [nameType, setNameType] = useState("");
  const [contactUsModalVisible, setContactUsModalVisible] = useState(false);

  /* Catalogue browsing state. Everything filters the list already in hand -
     the store has fetched the whole catalogue - so none of this calls the
     API. */
  /* Two values: `query` is what the field shows and updates on every
     keystroke, `search` is what the server has been asked for and lags by
     the debounce. Driving the request off the input would fire one request
     per character typed. */
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filterShape, setFilterShape] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterCase, setFilterCase] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 24;

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  /* Every one of these is a server query now. Searching or filtering
     client-side over a paginated list only ever looks at the rows already
     in the browser, so a product on page 3 would be invisible to a search
     for its own name. */
  const currentParams = useCallback(
    () => ({
      page,
      limit: PAGE_SIZE,
      search,
      shape: filterShape,
      vendor: filterVendor,
      case: filterCase,
    }),
    [page, search, filterShape, filterVendor, filterCase]
  );

  useEffect(() => {
    fetchData(currentParams());
  }, [currentParams, fetchData]);

  /* Re-issues the CURRENT view rather than a bare fetch. A plain
     fetchData() after saving or deleting would drop the page number and
     every filter, so editing a product on page 3 of a filtered list threw
     you back to an unfiltered page 1. */
  const reload = useCallback(
    () => fetchData(currentParams()),
    [fetchData, currentParams]
  );
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);
  
  // Suggestion modal state
  const [suggestionModalVisible, setSuggestionModalVisible] = useState(false);
  const [suggestionProducts, setSuggestionProducts] = useState([]);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [suggestionTotal, setSuggestionTotal] = useState(0);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [suggestionInputValue, setSuggestionInputValue] = useState("");
  const [suggestionSearchValue, setSuggestionSearchValue] = useState("");
  
  const [form] = Form.useForm();
  const [createNameForm] = Form.useForm();
  const [contactUsForm] = Form.useForm();
  const url = process.env.REACT_APP_API_BASE_URL;

  // Image preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  const token = localStorage.getItem("userToken");

  /*
   * The "if nothing loaded, fetch" effect that used to sit here is gone.
   *
   * Under pagination, products.length === 0 is a LEGITIMATE state - a
   * search or filter that matched nothing - not a sign the data failed to
   * load. It re-fired on every empty result, which both issued the same
   * request twice and clobbered the params effect, so clearing a search
   * never restored the list. The params effect above already covers mount
   * and every subsequent change.
   */

  const formatDate = (isoDateString) => {
    try {
      const date = new Date(isoDateString);
      if (isNaN(date.getTime())) throw new Error("Invalid date");
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "short" });
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const formattedHours = hours % 12 || 12;
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      return `${day} ${month} ${year}, ${formattedHours}:${formattedMinutes} ${ampm}`;
    } catch {
      return "Invalid Date";
    }
  };

  const handleOpenModal = (type, product = null) => {
    setModalType(type);
    setSelectedProduct(product);

    if (type === "edit" && product) {
      const existingFileList =
        product.images?.map((url, index) => ({
          uid: `existing-${index}`,
          name: `image-${index}.jpg`,
          status: "done",
          url,
        })) || [];

        form.setFieldsValue({
          ...product,
          suggestions: product.suggestions?.join(", ") || "",
        });
        
        setSuggestionInputValue(product.suggestions?.join(", ") || "");

        setSelectedImages(existingFileList);
    } else {
      form.resetFields();
      setSuggestionInputValue("");
      setSelectedImages([]);
    }

    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedProduct(null);
    form.resetFields();
    setSuggestionInputValue("");
    setSelectedImages([]);
  };

  const handleOpenCreateNameModal = (prefix, type) => {
    setApiPrefix(prefix);
    setNameType(type);
    setShowCreateNameModal(true);
    createNameForm.resetFields();
  };

  const handleCloseCreateNameModal = () => {
    setShowCreateNameModal(false);
    createNameForm.resetFields();
  };

  const handleOpenContactUsModal = () => {
    setContactUsModalVisible(true);
    contactUsForm.resetFields();
    setSelectedPdfFile(null);
  };

  const handleCloseContactUsModal = () => {
    setContactUsModalVisible(false);
    contactUsForm.resetFields();
    setSelectedPdfFile(null);
  };

  const handleOpenSuggestionModal = () => {
    const currentSuggestions = form.getFieldValue("suggestions");
    const suggestionsArray = currentSuggestions
      ? currentSuggestions.split(",").map((s) => s.trim()).filter((s) => s.length > 0)
      : [];
    setSelectedSuggestions(suggestionsArray);
    setSuggestionInputValue(currentSuggestions || "");
    setSuggestionSearchValue("");
    setSuggestionModalVisible(true);
    setSuggestionPage(1);
    fetchSuggestionProducts(1);
  };

  const handleCloseSuggestionModal = () => {
    setSuggestionModalVisible(false);
    setSuggestionProducts([]);
    setSuggestionPage(1);
  };

  const fetchSuggestionProducts = async (page = 1, search = "") => {
    setSuggestionLoading(true);
    try {
      const params = { page, limit: 10 };
      if (search) {
        params.search = search;
      }

      const response = await axios.get(`${url}/auth/v1/allProducts`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setSuggestionProducts(response.data.products || response.data.data || []);
      setSuggestionTotal(response.data.totalProducts || 0);
    } catch (error) {
      message.error("Failed to fetch products for suggestions");
    } finally {
      setSuggestionLoading(false);
    }
  };

  const handleSuggestionPageChange = (page) => {
    setSuggestionPage(page);
    fetchSuggestionProducts(page, suggestionSearchValue);
  };

  const handleSuggestionSearch = () => {
    setSuggestionPage(1);
    fetchSuggestionProducts(1, suggestionSearchValue);
  };

  const handleToggleSuggestion = (alphaCode) => {
    setSelectedSuggestions((prev) => {
      if (prev.includes(alphaCode)) {
        return prev.filter((code) => code !== alphaCode);
      } else {
        return [...prev, alphaCode];
      }
    });
  };

  const handleApplySuggestions = () => {
    const suggestionsText = selectedSuggestions.join(", ");
    form.setFieldsValue({
      suggestions: suggestionsText,
    });
    setSuggestionInputValue(suggestionsText);
    handleCloseSuggestionModal();
    message.success(`Applied ${selectedSuggestions.length} suggestion(s) successfully!`);
  };

  const handleCreateNameSubmit = async (values) => {
    try {
      await axios.post(
        `${url}/auth/v1/${apiPrefix}`,
        { name: values.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(`${nameType} created successfully!`);
      handleCloseCreateNameModal();
      reload();
    } catch (error) {
      message.error(`Failed to create ${nameType.toLowerCase()}`);
    }
  };

  const handleImageChange = ({ fileList }) => {
    setSelectedImages(fileList);
  };

  const handlePdfChange = ({ fileList }) => {
    if (fileList.length > 0) {
      setSelectedPdfFile(fileList[0]);
    } else {
      setSelectedPdfFile(null);
    }
  };

  const handlePdfUpload = async () => {
    if (!selectedPdfFile) {
      return null;
    }
    try {
      const formData = new FormData();
      formData.append("image", selectedPdfFile.originFileObj);
      const response = await axios.post(`${url}/auth/v1/upload-img`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data.fileUrl;
    } catch (error) {
      message.error("Failed to upload PDF file");
      return null;
    }
  };

  const handlePreviewCancel = () => setPreviewOpen(false);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url?.substring(file.url.lastIndexOf("/") + 1)
    );
  };

  const handleImageUpload = async () => {
    if (!selectedImages.length) {
      message.error("No images selected for upload");
      return [];
    }
    try {
      const uploadedImageUrls = [];

      for (const file of selectedImages) {
        if (file.status === "done" && file.url && !file.originFileObj) {
          uploadedImageUrls.push(file.url);
          continue;
        }

        if (file.originFileObj) {
          const formData = new FormData();
          formData.append("image", file.originFileObj);
          const response = await axios.post(
            `${url}/auth/v1/upload-img`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );
          uploadedImageUrls.push(response.data.fileUrl);
        }
      }

      message.success("Images uploaded successfully!");
      return uploadedImageUrls;
    } catch (error) {
      /* The server says why (no active subscription, file too large, S3
         rejected it). Swallowing it left "Failed to upload images" as the
         only clue for every distinct cause. */
      message.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to upload images"
      );
      return [];
    }
  };

  const handleContactUsSubmit = async (values) => {
    try {
      const data = {};

      if (values.googleDriveLink) {
        data.googleDriveLink = values.googleDriveLink;
      }

      if (selectedPdfFile) {
        const pdfUrl = await handlePdfUpload();
        if (pdfUrl) {
          data.pdfLink = pdfUrl;
        }
      }

      if (!data.googleDriveLink && !data.pdfLink) {
        message.error(
          "Please provide either a Google Drive link or upload a PDF file"
        );
        return;
      }

      await axios.post(`${url}/auth/v1/send-to-us`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Your message has been sent successfully!");
      handleCloseContactUsModal();
    } catch (error) {
      message.error("Failed to send message. Please try again.");
    }
  };

  const handleSubmit = async (values) => {
    try {
      const uploadedImageUrls = await handleImageUpload();
      const data = {
        ...values,
        images: uploadedImageUrls.length
          ? uploadedImageUrls
          : selectedProduct?.images || [],
        suggestions: values.suggestions
          ? values.suggestions.split(",").map((s) => s.trim())
          : [],
        createdDate:
          modalType === "add"
            ? new Date().toISOString()
            : selectedProduct?.createdDate,
      };

      if (modalType === "edit") {
        await axios.patch(
          `${url}/auth/v1/product/${selectedProduct._id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Product updated successfully!");
      } else {
        await axios.post(`${url}/auth/v1/product`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success("Product created successfully!");
      }

      handleModalClose();
      reload();
    } catch (error) {
      /* The server's message names the field or the limit that was hit
         (a required alphaCode, a duplicate name, the plan's product cap).
         The generic string hid all of them behind one sentence. */
      message.error(
        error.response?.data?.message ||
          `Failed to ${modalType === "edit" ? "update" : "create"} product`
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${url}/auth/v1/product/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      reload();
      message.success("Product deleted successfully!");
    } catch (error) {
      message.error("Failed to delete product");
    }
  };

  if (loading) {
    return <ProductTableSkeleton />;
  }

  /* Filtering the list already in hand rather than refetching: the store
     holds the whole catalogue. Matching is done over the fields someone
     would actually type - the name, its codes, and the attributes they can
     also filter by from the selects. */
  const filtering = Boolean(
    search || filterShape || filterVendor || filterCase
  );

  /* `products` IS the page now - the server sliced it - so there is no
     local filtering or slicing left to do. The client-side versions of
     both are gone rather than left in place: filtering rows the server
     already filtered is dead work, and slicing a page that is already a
     page would have shown 24 of 24. */
  const pageItems = products || [];
  const pageCount = totalPages;
  const safePage = Math.min(page, pageCount);

  const clearFilters = () => {
    setQuery("");
    setFilterShape("");
    setFilterVendor("");
    setFilterCase("");
    setPage(1);
  };

  let content;
  if (error) {
    content = (
      <div
        style={{
          marginTop: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "16px",
          textAlign: "center",
        }}
      >
        <Typography.Text type="danger" strong>
          {error || "Failed to fetch data"}
        </Typography.Text>

        <div style={{ display: "flex", gap: "12px" }}>
          <Button type="primary" onClick={() => setModalVisible(true)}>
            Add New Product
          </Button>
          <Button type="default" onClick={() => setContactUsModalVisible(true)}>
            Contact Us
          </Button>
        </div>
      </div>
    );
  } else {
    content = pageItems.length ? (
      <>
        <div className="pgrid">
          {pageItems.map((record) => {
            const cover = Array.isArray(record.images)
              ? record.images[0]
              : record.images;
            /* The mock tags every card with a category. Ours uses whichever
               of the record's own attributes exists rather than a fixed
               label, and renders nothing when neither does - the fields are
               optional on this data. */
            const tag = record.shape || record.case || "";
            const dims =
              record.width && record.height
                ? `${record.width} \u00d7 ${record.height} mm`
                : record.width || record.height || "\u2014";

            return (
              <article className="pcard" key={record._id}>
                <div className="pcard-figure">
                  {cover ? (
                    <img src={cover} alt={record.name} loading="lazy" />
                  ) : (
                    <span className="pcard-noimg">No photo</span>
                  )}
                  {tag && <span className="pcard-tag">{tag}</span>}
                </div>

                <div className="pcard-body">
                  <h3 className="pcard-name">{record.name}</h3>
                  {record.description && (
                    <p className="pcard-desc">{record.description}</p>
                  )}

                  <dl className="pcard-specs">
                    <div>
                      <dt>Vendor</dt>
                      <dd>{record.vendor || "\u2014"}</dd>
                    </div>
                    <div>
                      <dt>Dimensions</dt>
                      <dd>{dims}</dd>
                    </div>
                    <div>
                      <dt>Added</dt>
                      <dd>{formatDate(record.createdDate)}</dd>
                    </div>
                  </dl>
                </div>

                {/* The mock slides this row up from under the card on hover.
                    A hover-reveal is the only way to reach the action, which
                    no touch screen can do - so the actions are simply
                    always on the card. */}
                <div className="pcard-actions">
                  <Button
                    type="link"
                    className="pcard-action"
                    onClick={() => handleOpenModal("edit", record)}
                  >
                    View / edit
                  </Button>
                  <Popconfirm
                    title="Delete this product?"
                    onConfirm={() => handleDelete(record._id)}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    cancelText="Cancel"
                  >
                    <Button type="link" danger className="pcard-action">
                      Delete
                    </Button>
                  </Popconfirm>
                </div>
              </article>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className="pgrid-pager">
            <Pagination
              current={safePage}
              pageSize={PAGE_SIZE}
              total={total}
              onChange={setPage}
              showSizeChanger={false}
            />
          </div>
        )}

        {/* Support action, kept subordinate to the product-management
            actions in the toolbar above rather than competing with them. */}
        <div className="products-support">
          <Button
            type="link"
            size="small"
            icon={<MessageOutlined />}
            onClick={handleOpenContactUsModal}
          >
            Need help with your products? Contact us
          </Button>
        </div>
      </>
    ) : (
      <div className="products-empty">
        <InboxOutlined className="products-empty-icon" />
        <p className="products-empty-title">
          {filtering ? "No products match those filters" : "No products yet"}
        </p>
        <p className="products-empty-text">
          {filtering
            ? "Try a different search term, or clear the filters."
            : "Add your first product to start building your catalog."}
        </p>
        {filtering ? (
          <Button onClick={clearFilters}>Clear filters</Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal("add")}
          >
            Add Product
          </Button>
        )}
      </div>
    );
  }

  return (
    <main className="users-page">
      <div className="users-container">
        <header className="page-head">
          <div className="page-head-text">
            <h1 className="page-title">Products</h1>
            <p className="page-sub">
              Every tooth mould in your catalog, and the details clinics search
              by.
            </p>
          </div>
          <div className="page-head-actions">
            <Button
              icon={<FileExcelOutlined />}
              onClick={() => setBulkUploadVisible(true)}
            >
              Bulk upload
            </Button>
            <Button
              type="primary"
              className="products-add-btn"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal("add")}
            >
              Add product
            </Button>
          </div>
        </header>

        {/* Counts come straight from the product store, not invented.
            Same card shape as My Team: label on top, figure below. The two
            pages share .stat-row, so they have to share the markup too or
            one of them renders against styling written for the other. */}
        <div className="stat-row">
          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">
                {catalogueTotal === 1 ? "Product" : "Products"}
              </span>
            </div>
            {/* catalogueTotal, not products.length and not `total`. The
                first is only the current page; the second is the FILTERED
                count, which made this read "0 Products" to someone with a
                full catalogue whenever a search matched nothing. This card
                reports what the user owns. */}
            <span className="stat-value stat-value-accent">
              {catalogueTotal}
            </span>
          </div>
          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">
                {vendors.length === 1 ? "Manufacturer" : "Manufacturers"}
              </span>
            </div>
            <span className="stat-value">{vendors.length}</span>
          </div>
          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">
                {shapes.length === 1 ? "Shape" : "Shapes"}
              </span>
            </div>
            <span className="stat-value">{shapes.length}</span>
          </div>
          <div className="stat-tile">
            <div className="stat-head">
              <span className="stat-label">
                {cases.length === 1 ? "Case type" : "Case types"}
              </span>
            </div>
            <span className="stat-value">{cases.length}</span>
          </div>
        </div>

        {/* Search and filters. Options come from the catalogue's own
            values via the store, never a hardcoded list - these are free
            text fields, so the only honest options are the ones the data
            actually contains. */}
        <div className="pfilters">
          <div className="pfilters-search">
            <span className="pfilters-search-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="15" height="15">
                <circle
                  cx="7"
                  cy="7"
                  r="4.6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                />
                <path
                  d="m10.4 10.4 3.1 3.1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <input
              type="search"
              className="pfilters-input"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              placeholder="Search products, vendors or shapes"
              aria-label="Search products"
            />
          </div>

          <div className="pfilters-selects">
            <Select
              className="pfilters-select"
              value={filterShape || undefined}
              onChange={(v) => {
                setFilterShape(v || "");
                setPage(1);
              }}
              allowClear
              placeholder="All shapes"
            >
              {shapes.map((m) => (
                <Option key={m?.name || m} value={m?.name || m}>
                  {m?.name || m}
                </Option>
              ))}
            </Select>

            <Select
              className="pfilters-select"
              value={filterVendor || undefined}
              onChange={(v) => {
                setFilterVendor(v || "");
                setPage(1);
              }}
              allowClear
              placeholder="All vendors"
            >
              {vendors.map((m) => (
                <Option key={m?.name || m} value={m?.name || m}>
                  {m?.name || m}
                </Option>
              ))}
            </Select>

            <Select
              className="pfilters-select"
              value={filterCase || undefined}
              onChange={(v) => {
                setFilterCase(v || "");
                setPage(1);
              }}
              allowClear
              placeholder="All case types"
            >
              {cases.map((m) => (
                <Option key={m?.name || m} value={m?.name || m}>
                  {m?.name || m}
                </Option>
              ))}
            </Select>

            {filtering && (
              <button
                type="button"
                className="pfilters-clear"
                onClick={clearFilters}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {content}

        <BulkUploadModal
          open={bulkUploadVisible}
          onClose={() => setBulkUploadVisible(false)}
          onUploaded={fetchData}
        />

        <Modal
          title={modalType === "add" ? "Add New Product" : "Edit Product"}
          open={modalVisible}
          onCancel={handleModalClose}
          width={640}
          zIndex={2000}
          /* Centered rather than antd's default top:100px, which would put
             100px + 85vh past the bottom of the viewport. */
          centered
          className="product-modal"
          /* Header and footer sit outside the scrolling body, so the actions
             stay visible no matter how far the form is scrolled. */
          footer={
            <div className="product-modal-actions">
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button
                type="primary"
                className="product-modal-submit"
                onClick={() => form.submit()}
              >
                {modalType === "add" ? "Create Product" : "Update Product"}
              </Button>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ images: [] }}
            /* antd's default puts a red asterisk *before* the label, so
               required labels started ~11px right of optional ones and the
               label column read as ragged. Marking the four optional fields
               instead of the eight required ones fixes the alignment and
               cuts the number of marks in half. */
            requiredMark="optional"
          >
            {/* Primary identifier stays full width. */}
            <Form.Item
              label="Mold Name"
              name="name"
              rules={[{ required: true, message: "Please enter product name" }]}
            >
              <Input />
            </Form.Item>

            {/* Paired fields: xs={24} sm={12} collapses to one column
                below antd's 576px breakpoint. */}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Height (mm)"
                  name="height"
                  rules={[{ required: true, message: "Please enter height" }]}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Width (mm)"
                  name="width"
                  rules={[{ required: true, message: "Please enter width" }]}
                >
                  <Input type="number" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Vendor"
                  name="vendor"
                  rules={[{ required: true, message: "Please select a vendor" }]}
                >
                  <Select
                    onChange={(value) =>
                      value === "createVendor" &&
                      handleOpenCreateNameModal("vendor", "Vendor")
                    }
                  >
                    <Option value="">Select Vendor</Option>
                    {vendors.map((vendor) => (
                      <Option key={vendor} value={vendor}>
                        {vendor}
                      </Option>
                    ))}
                    <Option value="createVendor">Create New Vendor</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Shape"
                  name="shape"
                  rules={[{ required: true, message: "Please select a shape" }]}
                >
                  <Select
                    onChange={(value) =>
                      value === "createShape" &&
                      handleOpenCreateNameModal("shape", "Shape")
                    }
                  >
                    <Option value="">Select Shape</Option>
                    {shapes.map((shape) => (
                      <Option key={shape} value={shape}>
                        {shape}
                      </Option>
                    ))}
                    <Option value="createShape">Create New Shape</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Color"
                  name="color"
                  rules={[{ required: true, message: "Please select a color" }]}
                >
                  <Select
                    onChange={(value) =>
                      value === "createColor" &&
                      handleOpenCreateNameModal("color", "Color")
                    }
                  >
                    <Option value="">Select Color</Option>
                    {colors.map((color) => (
                      <Option key={color} value={color}>
                        {color}
                      </Option>
                    ))}
                    <Option value="createColor">Create New Color</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Case"
                  name="case"
                  rules={[{ required: true, message: "Please select a case" }]}
                >
                  <Select
                    onChange={(value) =>
                      value === "createCase" &&
                      handleOpenCreateNameModal("case", "Case")
                    }
                  >
                    <Option value="">Select Case</Option>
                    {cases.map((caseItem) => (
                      <Option key={caseItem} value={caseItem}>
                        {caseItem}
                      </Option>
                    ))}
                    <Option value="createCase">Create New Case</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                {/* Required + unique in the Product schema. The form marked
                    it optional, so a blank one reached the server and came
                    back as a bare "Failed to create product". */}
                <Form.Item
                  label="Alpha Code"
                  name="alphaCode"
                  rules={[
                    { required: true, message: "Please enter an alpha code" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item label="Mass (grams)" name="mass">
                  <Input type="number" />
                </Form.Item>
              </Col>
            </Row>

            {/* Input keeps the normal field rhythm; the helper action sits
                below it as a small secondary link instead of a full
                input-height button welded to the side. */}
            <Form.Item
              label="Suggestions (comma separated)"
              name="suggestions"
              className="product-modal-suggestions"
            >
              <Input
                placeholder="Alpha codes separated by commas"
                value={suggestionInputValue}
                onChange={(e) => {
                  setSuggestionInputValue(e.target.value);
                  form.setFieldsValue({ suggestions: e.target.value });
                }}
              />
            </Form.Item>
            <div className="product-modal-suggestions-action">
              <Button type="link" size="small" onClick={handleOpenSuggestionModal}>
                Pick from existing products
              </Button>
            </div>

            <Form.Item label="Description" name="description">
              <Input.TextArea rows={3} />
            </Form.Item>

            <Form.Item label="Images" name="images">
              <ImgCrop
                rotationSlider
                quality={0.9}
                modalTitle="Crop image"
              >
                <Upload
                  fileList={selectedImages}
                  onChange={handleImageChange}
                  onPreview={handlePreview}
                  beforeUpload={() => false}
                  multiple
                  accept="image/*"
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />}>Select Images</Button>
                </Upload>
              </ImgCrop>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={handlePreviewCancel}
        >
          <img loading="lazy" alt="preview" style={{ width: "100%" }} src={previewImage} />
        </Modal>

        <Modal
          title={`Create New ${nameType}`}
          open={showCreateNameModal}
          onCancel={handleCloseCreateNameModal}
          footer={null}
          zIndex={2000}
        >
          <Form
            form={createNameForm}
            layout="vertical"
            onFinish={handleCreateNameSubmit}
          >
            <Form.Item
              label={`${nameType} Name`}
              name="name"
              rules={[
                {
                  required: true,
                  message: `Please enter ${nameType.toLowerCase()} name`,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Create {nameType}
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={handleCloseCreateNameModal}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Contact Us"
          open={contactUsModalVisible}
          onCancel={handleCloseContactUsModal}
          footer={null}
          width={600}
          zIndex={2000}
        >
          <Form
            form={contactUsForm}
            layout="vertical"
            onFinish={handleContactUsSubmit}
          >
            <Form.Item
              label="Google Drive Link"
              name="googleDriveLink"
              rules={[
                {
                  pattern:
                    /^https:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+\/view.*$/,
                  message: "Please enter a valid Google Drive file link",
                },
              ]}
            >
              <Input placeholder="Enter Google Drive Link" />
            </Form.Item>

            <Form.Item label="Upload PDF File" name="pdfFile">
              <Upload
                fileList={selectedPdfFile ? [selectedPdfFile] : []}
                onChange={handlePdfChange}
                beforeUpload={() => false}
                accept=".pdf"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select PDF File</Button>
              </Upload>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Send Message
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={handleCloseContactUsModal}
              >
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title="Select Suggested Products"
          open={suggestionModalVisible}
          onCancel={handleCloseSuggestionModal}
          width={750}
          zIndex={2000}
          footer={[
            <Button key="cancel" onClick={handleCloseSuggestionModal}>
              Cancel
            </Button>,
            <Button
              key="apply"
              type="primary"
              onClick={handleApplySuggestions}
            >
              Apply Suggestions
            </Button>,
          ]}
        >
          <div style={{ marginBottom: 16 }}>
            <Typography.Text strong>
              Selected: {selectedSuggestions.length} product(s)
            </Typography.Text>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Input.Group compact>
              <Input
                style={{ width: "calc(100% - 100px)" }}
                placeholder="Search products by name or alpha code"
                value={suggestionSearchValue}
                onChange={(e) => setSuggestionSearchValue(e.target.value)}
                onPressEnter={handleSuggestionSearch}
              />
              <Button
                type="primary"
                onClick={handleSuggestionSearch}
                style={{ width: "100px" }}
              >
                Search
              </Button>
            </Input.Group>
          </div>
          
          <Table
            loading={suggestionLoading}
            dataSource={suggestionProducts}
            rowKey="_id"
            pagination={false}
            scroll={{ x: 650 }}
            size="small"
            columns={[
              {
                title: "Select",
                key: "select",
                width: 60,
                render: (_, record) => (
                  <Checkbox
                    checked={selectedSuggestions.includes(record.alphaCode)}
                    onChange={() => handleToggleSuggestion(record.alphaCode)}
                  />
                ),
              },
              {
                title: "Image",
                key: "image",
                width: 60,
                render: (_, record) => (
                  <Image
                    src={record.images?.[0] || "https://via.placeholder.com/40"}
                    alt={record.name}
                    width={40}
                    height={40}
                    style={{ objectFit: "cover", borderRadius: 4 }}
                  />
                ),
              },
              {
                title: "Product Name",
                dataIndex: "name",
                key: "name",
                width: 150,
                ellipsis: true,
              },
              {
                title: "Alpha Code",
                dataIndex: "alphaCode",
                key: "alphaCode",
                width: 100,
              },
              {
                title: "H (mm)",
                dataIndex: "height",
                key: "height",
                width: 70,
              },
              {
                title: "W (mm)",
                dataIndex: "width",
                key: "width",
                width: 70,
              },
              {
                title: "Vendor",
                dataIndex: "vendor",
                key: "vendor",
                width: 100,
                ellipsis: true,
              },
            ]}
          />
          
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Pagination
              current={suggestionPage}
              total={suggestionTotal}
              pageSize={10}
              onChange={handleSuggestionPageChange}
              showSizeChanger={false}
              showTotal={(total) => `Total ${total} products`}
            />
          </div>
        </Modal>
      </div>
    </main>
  );
};

export default Products;